/** @jsx jsx */
import { jsx } from '@emotion/react';
import { AccountsMenuLinks, moveZapsLink } from '../../constants';

import type { UniversalNavLinkType } from '../../constants';
import {
  useUniversalNavContext,
  useQuerySession,
  useQueryAccountsList,
} from '../../hooks';

import AccountsMenu from './AccountsMenu';
import type { Props as AccountsMenuProps } from './AccountsMenu';

type Account = {
  id: number;
  normalizedImageUrl: string;
  normalizedName: string;
  accountSwitcherUrl?: string;
};

type Props = Omit<
  AccountsMenuProps,
  'accounts' | 'email' | 'menuLabel' | 'menuLinks'
>;

/**
 * Renders `AccountsMenu` with accounts data
 */
const AccountsMenuContainer = (props: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const { data: accountsData } = useQueryAccountsList();
  const { data: sessionData } = useQuerySession();

  // The display name for an account is account owner's name
  const accounts = (accountsData || []).map((account: Account) => ({
    id: account.id,
    name: account.normalizedName,
    imageUrl: account.normalizedImageUrl,
    accountSwitcherUrl: getFullyQualifiedURL(
      `/switch-account?account=${account.id}&next=${window.location.href}`
    ) as string,
  }));

  const menuLinks: Array<UniversalNavLinkType> = [
    ...AccountsMenuLinks.map((link) => ({
      ...link,
      href: getFullyQualifiedURL(link.href) || null,
    })),
  ];
  // This is an annoying workaround because TS doesn't like adding
  // `moveZapsLink` in a ternary and then calling `.filter(Boolean)`
  // to filter it out if it doesn't exist. There's a way around it
  // but it's syntactically confusing.
  if (accounts.length > 1) {
    menuLinks.unshift(moveZapsLink);
  }

  const email = sessionData?.email || '';

  return (
    <AccountsMenu
      accounts={accounts}
      email={email}
      menuLinks={menuLinks}
      {...props}
    />
  );
};

export default AccountsMenuContainer;
